import { useEffect, useState, type ReactNode } from "react"
import { ArrowRight, ChevronDown, Settings } from "lucide-react"
import { cn } from "@/lib/utils"
import { BottomSheet } from "../BottomSheet/BottomSheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../DropdownMenu/DropdownMenu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover"

const TIMEFRAMES = ["M1", "M5", "M15", "M30", "H1", "H4", "D1", "W1"] as const

function useIsSmall() {
  const [isSmall, setIsSmall] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false,
  )
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)")
    setIsSmall(mq.matches)
    const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])
  return isSmall
}

function formatDateShort(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const month = d.toLocaleString("en-US", { month: "short" })
  return `${month} ${d.getDate()}, ${d.getFullYear()}`
}

export interface ChartConfigBarProps {
  symbol: string
  timeframe: string
  onTimeframeChange: (tf: string) => void
  dateFrom: string
  dateTo: string
  timeframes?: readonly string[]
  /** Display labels per timeframe id (e.g. H1 -> "1h"). Falls back to the id. */
  timeframeLabels?: Record<string, string>
  statusSlot?: ReactNode
  rightSlot?: ReactNode
  /**
   * Optional extra configuration content rendered:
   * - inside the mobile drawer, below the basic-params block
   * - inside a Popover anchored to a new Settings button on desktop
   * If omitted, the desktop Settings button is hidden entirely.
   */
  extraConfigSlot?: ReactNode
  className?: string
}

export function ChartConfigBar({
  symbol,
  timeframe,
  onTimeframeChange,
  dateFrom,
  dateTo,
  timeframes = TIMEFRAMES,
  timeframeLabels,
  statusSlot,
  rightSlot,
  extraConfigSlot,
  className,
}: ChartConfigBarProps) {
  const isSmall = useIsSmall()
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <div
      className={cn(
        "flex items-center gap-3 px-4 border-b border-border shrink-0 min-h-11 py-2 bg-background",
        className,
      )}
    >
      {isSmall ? (
        <>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="See configuration"
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0"
          >
            <Settings size={12} strokeWidth={1.75} />
          </button>
          <TimeframeSelect
            value={timeframe}
            onChange={onTimeframeChange}
            timeframes={timeframes}
            labels={timeframeLabels}
          />
          <div className="flex-1 min-w-0 flex items-center overflow-hidden">{statusSlot}</div>
          {rightSlot}
          <BottomSheet
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ariaLabel="Backtest configuration"
          >
            <div className="flex flex-col h-full min-h-0">
              <div className="shrink-0 px-4 pt-2 pb-3">
                <h2 className="text-section text-foreground">
                  Backtest configuration
                </h2>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-6">
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center border-b border-border py-2">
                    <span className="text-label text-muted-foreground">Tick Data</span>
                    <span className="text-eyebrow font-semibold px-1.5 py-0.5 rounded bg-muted border border-border text-muted-foreground">
                      PRO
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-border py-2">
                    <span className="text-label text-muted-foreground">Market</span>
                    <span className="text-body font-medium text-foreground">{symbol}</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-border py-2">
                    <span className="text-label text-muted-foreground">Range</span>
                    <span className="text-body text-foreground inline-flex items-center gap-1.5 whitespace-nowrap">
                      {formatDateShort(dateFrom)}
                      <ArrowRight size={10} strokeWidth={2} className="text-muted-foreground" />
                      {formatDateShort(dateTo)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-b border-border py-2">
                    <span className="text-label text-muted-foreground">Timeframe</span>
                    <span className="text-body font-medium text-foreground">
                      {timeframeLabels?.[timeframe] ?? timeframe}
                    </span>
                  </div>
                </div>

                {extraConfigSlot && (
                  <div className="mt-4">{extraConfigSlot}</div>
                )}
              </div>
            </div>
          </BottomSheet>
        </>
      ) : (
        <>
          {extraConfigSlot && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="See configuration"
                  className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer shrink-0"
                >
                  <Settings size={12} strokeWidth={1.75} />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={8}
                className="w-96 min-w-[22rem] max-w-[26rem] max-h-[70dvh] overflow-y-auto p-3"
              >
                <h2 className="text-label font-semibold text-foreground mb-3">
                  Backtest configuration
                </h2>
                {extraConfigSlot}
              </PopoverContent>
            </Popover>
          )}
          <div className="flex items-center gap-x-3 gap-y-1.5 min-w-0 flex-1 flex-wrap">
            <TickDataPro />
            <ReadOnlyValue label="Market" value={symbol} />
            <DateRange from={dateFrom} to={dateTo} />
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-caption text-muted-foreground">Timeframe</span>
              <TimeframeSelect
                value={timeframe}
                onChange={onTimeframeChange}
                timeframes={timeframes}
                labels={timeframeLabels}
              />
            </div>
          </div>
          {statusSlot}
          {rightSlot}
        </>
      )}
    </div>
  )
}

interface TimeframeSelectProps {
  value: string
  onChange: (tf: string) => void
  timeframes: readonly string[]
  labels?: Record<string, string>
}

function TimeframeSelect({ value, onChange, timeframes, labels }: TimeframeSelectProps) {
  const label = (tf: string) => labels?.[tf] ?? tf
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-caption font-medium text-foreground bg-muted border border-border hover:border-border-medium transition-colors cursor-pointer shrink-0"
        >
          {label(value)}
          <ChevronDown size={11} strokeWidth={1.75} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="v1 min-w-[5rem] p-1">
        {timeframes.map((tf) => (
          <DropdownMenuItem
            key={tf}
            onSelect={() => onChange(tf)}
            className={cn(
              "text-caption justify-center tabular-nums cursor-pointer",
              tf === value && "bg-muted text-foreground font-medium",
            )}
          >
            {label(tf)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function TickDataPro() {
  return (
    <div className="flex items-center shrink-0 gap-1.5">
      <span className="font-medium text-muted-foreground text-caption">Tick Data</span>
      <span className="font-semibold rounded bg-muted border border-border text-muted-foreground text-eyebrow px-1.5 py-0.5">
        PRO
      </span>
    </div>
  )
}

interface ReadOnlyValueProps {
  label: string
  value: string
}

function ReadOnlyValue({ label, value }: ReadOnlyValueProps) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-caption text-muted-foreground">{label}</span>
      <span className="text-caption font-medium text-foreground">{value}</span>
    </div>
  )
}

interface DateRangeProps {
  from: string
  to: string
}

function DateRange({ from, to }: DateRangeProps) {
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-caption text-muted-foreground">Range</span>
      <span className="text-caption text-foreground whitespace-nowrap">{formatDateShort(from)}</span>
      <ArrowRight size={10} strokeWidth={2} className="text-muted-foreground" />
      <span className="text-caption text-foreground whitespace-nowrap">{formatDateShort(to)}</span>
    </div>
  )
}
